/**
 * ДКПП picker для номенклатури-послуг (2026-06-05).
 *
 * Дзеркало UktzedPicker, але для ДК 016:2010 (ДКПП — класифікатор послуг).
 * «Код для ПН»: товари → УКТЗЕД, послуги → ДКПП. Одне поле ukt_zed_code.
 *
 * Search: GET /api/v1/refs/dkpp-codes?q=... (ILIKE + trigram).
 * Код variable-length (68 / 68.2 / 68.20 / 68.20.11).
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dkppCodes } from '@/api/catalogs';


interface Props {
  value: string | null;
  onChange: (code: string | null, name: string | null) => void;
  disabled?: boolean;
}

export default function DkppPicker({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // ДКПП-код: цифри + крапки (68.20). Інакше — текстовий пошук.
  const isCodeSearch = /^[\d.]+$/.test(q);
  const searchParams = isCodeSearch
    ? { code_prefix: q, limit: 30 }
    : { q: q || undefined, limit: 30 };

  const searchQ = useQuery({
    queryKey: ['dkpp-search', q, isCodeSearch],
    queryFn: () => dkppCodes.search(searchParams),
    enabled: open && q.length >= 2,
  });

  // Lookup поточного коду для display (показуємо назву поруч).
  const currentQ = useQuery({
    queryKey: ['dkpp-current', value],
    queryFn: () => dkppCodes.search({ code_prefix: value!, limit: 1 }),
    enabled: !!value,
  });
  const currentName =
    currentQ.data?.items?.find((i) => i.code === value)?.name_uk ?? null;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const displayText = value
    ? `${value}${currentName ? ` — ${currentName}` : ''}`
    : 'ДКПП (натисніть для пошуку)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        readOnly
        disabled={disabled}
        value={displayText}
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          setQ('');
        }}
        placeholder="Пошук за кодом/назвою послуги…"
        style={{ width: '100%', cursor: disabled ? 'not-allowed' : 'pointer' }}
        title={currentName ?? undefined}
      />
      {value && !disabled && (
        <button
          onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
          style={{
            position: 'absolute', right: 4, top: 4,
            padding: '0.1rem 0.4rem', fontSize: '0.7rem',
          }}
          title="Очистити"
        >
          ×
        </button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
          background: 'white', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', marginTop: 4,
          maxHeight: 400, overflowY: 'auto', boxShadow: 'var(--shadow-lg)',
        }}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Введіть мін. 2 символи: код або частину назви…"
            style={{
              width: '100%', padding: '0.5rem', border: 'none',
              borderBottom: '1px solid var(--border)', borderRadius: 0,
            }}
          />
          {q.length < 2 ? (
            <div style={{ padding: '0.6rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              Введіть запит (≥ 2 символи). Шукати можна за:
              <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                <li>Код: <code>68.20</code> (оренда)</li>
                <li>Назва: <code>оренда</code>, <code>бухгалтерськ</code>, <code>консультац</code></li>
              </ul>
            </div>
          ) : (searchQ.data?.items ?? []).length === 0 ? (
            <div style={{ padding: '0.6rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              {searchQ.isLoading ? 'Шукаю…' : 'Нічого не знайдено.'}
            </div>
          ) : (
            (searchQ.data!.items).map((e) => (
              <div
                key={e.code}
                onClick={() => { onChange(e.code, e.name_uk); setOpen(false); }}
                style={{
                  padding: '0.5rem 0.6rem', cursor: 'pointer',
                  borderTop: '1px solid var(--border-soft)', fontSize: '0.85rem',
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.background = 'var(--bg-soft)')}
                onMouseLeave={(ev) => (ev.currentTarget.style.background = 'white')}
              >
                <span style={{
                  fontFamily: 'monospace', fontWeight: 700, marginRight: '0.5rem',
                }}>
                  {e.code}
                </span>
                {e.name_uk}
              </div>
            ))
          )}
          {searchQ.data && searchQ.data.total > 30 && (
            <div style={{
              padding: '0.4rem 0.6rem', fontSize: '0.75rem',
              color: 'var(--muted)', borderTop: '1px solid var(--border-soft)',
            }}>
              Знайдено {searchQ.data.total} результатів. Уточніть запит.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
